import { Icon } from "@iconify/react";
import {
  CellEditorSelectorResult,
  ColDef,
  ICellEditorParams,
  ValueSetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import {
  addEmplooyeesViaExcel,
  commitBulkEmployeeImport,
  getEmployeeTemplate,
  validateBulkEmployeeImport,
} from "../../api/Esop";
import {
  Box,
  ButtonSize,
  HStack,
  PrimaryCTAButton,
  SecondaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../components/utils";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/shared/Card";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Input, Label } from "../../components/shared/InputField";
import { Instructions } from "../../components/shared/Instructions";
import { Select } from "../../components/shared/Select";
import SearchDropDown from "../../components/shared/SearchDropdown";
import { SummaryMetricCard } from "../../components/shared/SummaryMetricCard";
import Tooltip from "../../components/shared/Tooltip";
import { NOMINEE_RELATIONSHIP_OPTIONS } from "../../constants/NomineeConstants";
import { useGetEmployeeUploadConfigs } from "../../queries/employees";
import convertToBase64 from "../../utils/convertToBase64";
import { downloadBlobObject } from "../../utils/DownloadFile";
import { formatDisplayDate } from "../../utils/date";
import { normalizeEmployeeTags } from "../../utils/employeePatch";
import {
  EmployeeImportReviewTab,
  getEmployeeImportReviewSummary,
  getEmployeeImportRowsForTab,
  isEmployeeImportCommitEligible,
} from "../../utils/employeeImportReview";
import { toEmployeeImportInput } from "../../utils/employeeImportPayload";
import BCHeader from "../../shared/BCHeader";
import {
  BoomerangCandidate,
  BoomerangMatchReason,
  BoomerangResolution,
  BoomerangResolutionStrategy,
  BulkEmployeeImportCommitResult,
  BulkEmployeeImportRow,
  BulkEmployeeImportValidationResult,
  EmployeeUploadHeaderResponse,
  ImportConfig,
} from "../../types/Employee";
import DarwinBoxDatasetImport from "./DarwinBoxDatasetImport";
import GreyTipImport from "./GreyTipImport";
import {
  BulkBoomerangResolutionDialog,
  BulkResolutionChoice,
} from "./BulkBoomerangResolutionDialog";

const SYSTEM_FIELD_LABELS: Record<string, string> = {
  name: "Employee name",
  employeeIdentification: "Employee ID",
  email: "Work email",
  personalEmail: "Personal email",
  designation: "Designation",
  department: "Department",
  dateOfJoining: "Date of joining",
  dematAccountId1: "Demat account ID 1",
  dematAccountId2: "Demat account ID 2",
  pan: "PAN",
  contact1: "Nominee 1 contact",
  address1: "Nominee 1 address",
  contact2: "Nominee 2 contact",
  address2: "Nominee 2 address",
};

const EXCEL_REUPLOAD_REQUIRED_MESSAGE = "Upload the Excel file again and retry";

const MATCH_REASON_LABELS: Record<BoomerangMatchReason, string> = {
  WORK_EMAIL: "Work email",
  PERSONAL_EMAIL: "Personal email",
  EMPLOYEE_ID: "Employee ID",
  NAME: "Name",
};

const RESOLUTION_STRATEGY_OPTIONS: Array<{
  value: BoomerangResolutionStrategy;
  label: string;
}> = [
  { value: "NOT_BOOMERANG", label: "Not a boomerang" },
  { value: "MERGE_EXISTING_EMPLOYEE", label: "Merge record" },
  { value: "CREATE_SEPARATE_EMPLOYEE", label: "Link and create" },
];

const NOMINEE_PREFERRED_ORDER = [
  "nominee1",
  "relationship1",
  "percentage1",
  "dob1",
  "contact1",
  "email1",
  "address1",
  "guardianName1",
  "guardianRelationship1",
  "guardianDob1",
  "guardianContact1",
  "guardianEmail1",
  "guardianAddress1",
  "nominee2",
  "relationship2",
  "percentage2",
  "dob2",
  "contact2",
  "email2",
  "address2",
  "guardianName2",
  "guardianRelationship2",
  "guardianDob2",
  "guardianContact2",
  "guardianEmail2",
  "guardianAddress2",
];

const DATE_FIELDS = new Set([
  "dateOfJoining",
  "dateOfJoin",
  "lastDay",
  "resignationDate",
  "dob1",
  "dob2",
  "guardianDob1",
  "guardianDob2",
]);

const HIDDEN_REVIEW_FIELDS = new Set([
  "companyId",
  "isPausedVesting",
  "customFieldValues",
  "dematAccounts",
  "nominees",
  "boomerangResolution",
  "employeeName",
  "employeeDesignation",
  "dateOfJoin",
]);

type ReviewTab = EmployeeImportReviewTab;
type ImportSource = "EXCEL" | "GREYTIP" | "DARWINBOX";

const IMPORT_SOURCES: Array<{
  id: ImportSource;
  title: string;
  description: string;
  actionLabel: string;
  logo: string;
}> = [
  {
    id: "GREYTIP",
    title: "Import from GreyTip",
    description: "Connect to GreyTip to import your employee records.",
    actionLabel: "Connect GreyTip",
    logo: "/greytip.png",
  },
  {
    id: "EXCEL",
    title: "Import from Excel",
    description:
      "Upload an Excel file, map fields, and validate employee data.",
    actionLabel: "Import Excel",
    logo: "/excel.png",
  },
  {
    id: "DARWINBOX",
    title: "Import from Darwinbox",
    description: "Connect to Darwinbox to import your employee records.",
    actionLabel: "Connect Darwinbox",
    logo: "https://equion-dev.s3.us-west-2.amazonaws.com/assets/darwinbox.jpg",
  },
];

function formatFieldName(fieldName: string) {
  return fieldName
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (character) => character.toUpperCase())
    .trim();
}

function getPrefilledMapping(response: EmployeeUploadHeaderResponse) {
  const prefilled = new Map<string, string>();
  Object.entries(response.prefilledMapperData || {}).forEach(
    ([field, config]) => {
      if (config.type === "direct" && config.field)
        prefilled.set(config.field, field);
    }
  );

  // Support common PAN headers in templates created before the server-side
  // mapper recognized them.
  if (response.headersInSystem.includes("pan")) {
    const panHeader = response.headersInFile.find((header) =>
      ["pan", "pan number", "permanent account number"].includes(
        header.trim().toLowerCase().replace(/\s+/g, " ")
      )
    );
    if (panHeader && !prefilled.has(panHeader)) prefilled.set(panHeader, "pan");
  }

  return prefilled;
}

function formatCandidateDate(value?: string) {
  return value
    ? new Intl.DateTimeFormat("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "Not recorded";
}

function getEmployeeName(row: BulkEmployeeImportRow): string {
  const name = normalizeReviewCellValue(row.employee.name);
  if (typeof name === "string" && name) return name;

  const employeeName = normalizeReviewCellValue(row.employee.employeeName);
  return typeof employeeName === "string" ? employeeName : "";
}

function normalizeReviewCellValue(value: unknown) {
  if (value === undefined || value === null) return "";
  if (
    typeof value === "string" &&
    ["undefined", "null"].includes(value.trim().toLowerCase())
  ) {
    return "";
  }
  return value;
}

function formatReviewCellValue(value: unknown): string | number | boolean {
  const normalizedValue = normalizeReviewCellValue(value);
  if (normalizedValue === "") return "";
  if (
    typeof normalizedValue === "string" ||
    typeof normalizedValue === "number" ||
    typeof normalizedValue === "boolean"
  ) {
    return normalizedValue;
  }

  try {
    return JSON.stringify(normalizedValue) ?? String(normalizedValue);
  } catch {
    return String(normalizedValue);
  }
}

function isDatePreviewField(header: string, mappedField?: string) {
  return (
    DATE_FIELDS.has(mappedField || "") ||
    /(?:^|[\s_-])(date|day|dob)(?:$|[\s_-]|\d)/i.test(
      header.replace(/([a-z])([A-Z])/g, "$1 $2")
    )
  );
}

function formatExcelSampleValue(
  header: string,
  value: unknown,
  mappedField?: string
) {
  const normalizedValue = normalizeReviewCellValue(value);
  if (normalizedValue === "") return "";

  if (!isDatePreviewField(header, mappedField)) {
    return String(normalizedValue);
  }

  const numberValue = Number(normalizedValue);
  const date =
    Number.isFinite(numberValue) && numberValue >= 1 && numberValue <= 100000
      ? new Date(Date.UTC(1899, 11, 30) + numberValue * 24 * 60 * 60 * 1000)
      : new Date(String(normalizedValue));

  return Number.isNaN(date.getTime())
    ? String(normalizedValue)
    : formatDisplayDate(date);
}

function isNomineePercentageField(field: string) {
  return field === "percentage1" || field === "percentage2";
}

function toSpreadsheetPercentage(value: unknown) {
  const normalizedValue = normalizeReviewCellValue(value);
  if (normalizedValue === "") return normalizedValue;

  const percentage = Number(normalizedValue);
  return Number.isFinite(percentage) && percentage >= 0 && percentage <= 1
    ? percentage * 100
    : normalizedValue;
}

function getFieldValue(row: BulkEmployeeImportRow, field: string) {
  if (field === "__employeeName") return getEmployeeName(row);
  if (field === "tags") {
    return normalizeEmployeeTags(row.employee.tags)
      .map((tag) => tag.label)
      .join(", ");
  }
  if (field.startsWith("udf:")) {
    return normalizeReviewCellValue(
      row.employee.customFieldValues?.[field.slice(4)]
    );
  }
  if (isNomineePercentageField(field)) {
    return toSpreadsheetPercentage(row.employee[field]);
  }
  return normalizeReviewCellValue(row.employee[field]);
}

function updateRowField(
  row: BulkEmployeeImportRow,
  field: string,
  value: string
): BulkEmployeeImportRow {
  const employee = { ...row.employee };
  const validationField = field === "__employeeName" ? "name" : field;
  if (field === "__employeeName") {
    employee.name = value;
    employee.employeeName = value;
  } else if (field.startsWith("udf:")) {
    employee.customFieldValues = {
      ...(employee.customFieldValues || {}),
      [field.slice(4)]: value,
    };
  } else {
    const linkedFields: Record<string, string[]> = {
      name: ["name", "employeeName"],
      employeeName: ["name", "employeeName"],
      designation: ["designation", "employeeDesignation"],
      employeeDesignation: ["designation", "employeeDesignation"],
      address: ["address", "registeredAddress"],
      registeredAddress: ["address", "registeredAddress"],
      dateOfJoining: ["dateOfJoining", "dateOfJoin"],
      dateOfJoin: ["dateOfJoining", "dateOfJoin"],
      employeeIdentification: ["employeeIdentification", "hrId"],
      hrId: ["employeeIdentification", "hrId"],
    };
    (linkedFields[field] || [field]).forEach((linkedField) => {
      employee[linkedField] = value;
    });
  }
  return {
    ...row,
    employee,
    ...([
      "__employeeName",
      "email",
      "personalEmail",
      "employeeIdentification",
    ].includes(field)
      ? { resolution: undefined, selectedCandidateId: undefined }
      : {}),
    validationData: row.validationData.map((item) =>
      item.name === validationField || item.name === field
        ? { ...item, value, valid: true, error: null }
        : item
    ),
  };
}

function statusLabel(status: BulkEmployeeImportRow["status"]) {
  return {
    VALID: "Valid",
    ERROR: "Needs correction",
    ACTIVE_DUPLICATE: "Active duplicate",
    BOOMERANG_CANDIDATE: "Boomerang",
    RESOLVED_BOOMERANG: "Resolved rejoin",
  }[status];
}

function statusClass(status: BulkEmployeeImportRow["status"]) {
  return {
    VALID: "bg-green-50 text-green-700",
    ERROR: "bg-red-50 text-red-700",
    ACTIVE_DUPLICATE: "bg-red-50 text-red-700",
    BOOMERANG_CANDIDATE: "bg-orange-50 text-primary",
    RESOLVED_BOOMERANG: "bg-blue-50 text-secondary",
  }[status];
}

function importResultStrategyLabel(
  strategy: BulkEmployeeImportCommitResult["rows"][number]["strategy"]
) {
  return {
    NEW_EMPLOYEE: "New employee",
    NOT_BOOMERANG: "New employee",
    MERGE_EXISTING_EMPLOYEE: "Merged rejoin",
    CREATE_SEPARATE_EMPLOYEE: "Separate rejoin",
  }[strategy];
}

function StatusCell({ data }: { data: BulkEmployeeImportRow }) {
  const errors = data.validationData
    .filter((field) => !field.valid && field.error)
    .map((field) => field.error);
  return (
    <VStack className="gap-1 py-1">
      <span
        className={`inline-flex w-fit rounded-lg px-2 py-1 text-xs font-medium ${statusClass(
          data.status
        )}`}
      >
        {statusLabel(data.status)}
      </span>
      {errors[0] && <span className="text-xs text-red-700">{errors[0]}</span>}
    </VStack>
  );
}

function EditableCell({
  value,
  data,
  colDef,
}: {
  value: unknown;
  data: BulkEmployeeImportRow;
  colDef: ColDef;
}) {
  const field = String(colDef.field || "");
  const error = data.validationData.find(
    (item) =>
      item.name === field ||
      (field === "__employeeName" && item.name === "name")
  )?.error;
  const formattedValue =
    DATE_FIELDS.has(field) && value
      ? formatExcelSampleValue("", value, field)
      : formatReviewCellValue(value);
  return (
    <VStack className="gap-1 py-1">
      <span className={error ? "text-red-700" : "text-primaryText"}>
        {formattedValue as React.ReactNode}
      </span>
      {error && <span className="text-xs text-red-700">{error}</span>}
    </VStack>
  );
}

function getSelectedBoomerangCandidate(row: BulkEmployeeImportRow) {
  const candidateId =
    row.selectedCandidateId || row.resolution?.candidateEmployeeId;
  return row.candidates.find((item) => item.employeeId === candidateId);
}

function formatBoomerangMatchingCriteria(candidate: BoomerangCandidate) {
  return candidate.matchReasons
    .map((reason) => MATCH_REASON_LABELS[reason])
    .join(" + ");
}

function formatBoomerangCandidate(candidate: BoomerangCandidate) {
  const matchingCriteria = formatBoomerangMatchingCriteria(candidate);
  const identity = [candidate.employeeName, candidate.employeeIdentification]
    .filter(Boolean)
    .join(" · ");
  return matchingCriteria ? [identity, matchingCriteria].join(" — ") : identity;
}

function createBoomerangResolution(
  candidate: BoomerangCandidate,
  strategy: BoomerangResolutionStrategy,
  options: { moveGrants?: boolean; continueVesting?: boolean; reason?: string }
): BoomerangResolution {
  const base = {
    candidateEmployeeId: candidate.employeeId,
    expectedCandidateUpdatedAt: candidate.updatedAt,
    strategy,
    ...(options.reason?.trim() ? { reason: options.reason.trim() } : {}),
  };
  if (strategy === "MERGE_EXISTING_EMPLOYEE") {
    return {
      ...base,
      strategy,
      continueVesting: Boolean(options.continueVesting),
    };
  }
  if (strategy === "CREATE_SEPARATE_EMPLOYEE") {
    const moveGrants = Boolean(options.moveGrants);
    return {
      ...base,
      strategy,
      moveGrants,
      continueVesting: moveGrants && Boolean(options.continueVesting),
    };
  }
  return { ...base, strategy };
}

function applySingleCandidateDefaults(rows: BulkEmployeeImportRow[]) {
  return rows.map((row) => {
    if (
      row.status !== "BOOMERANG_CANDIDATE" ||
      row.resolution ||
      row.candidates.length !== 1
    ) {
      return row;
    }
    const candidate = row.candidates[0];
    return {
      ...row,
      selectedCandidateId: candidate.employeeId,
      resolution: createBoomerangResolution(candidate, "NOT_BOOMERANG", {}),
      status: "RESOLVED_BOOMERANG" as const,
    };
  });
}

function isEmployeeImportSelectable(row: BulkEmployeeImportRow) {
  return (
    isEmployeeImportCommitEligible(row) || row.status === "BOOMERANG_CANDIDATE"
  );
}

type EmployeeImportReviewGridProps = {
  rows: BulkEmployeeImportRow[];
  allRows: BulkEmployeeImportRow[];
  showBoomerangColumns: boolean;
  selectedRowIds: string[];
  udfFieldLabels: Record<string, string>;
  onSelectionChange: (rowIds: string[]) => void;
  onEdit: (row: BulkEmployeeImportRow) => void;
  onCandidateChange: (row: BulkEmployeeImportRow, candidateId: string) => void;
  onResolutionChange: (
    row: BulkEmployeeImportRow,
    strategy: BoomerangResolutionStrategy
  ) => void;
  onGrantDecisionChange: (
    row: BulkEmployeeImportRow,
    decision: "moveGrants" | "continueVesting",
    value: boolean
  ) => void;
};

function EmployeeImportReviewGrid({
  rows,
  allRows,
  showBoomerangColumns,
  selectedRowIds,
  udfFieldLabels,
  onSelectionChange,
  onEdit,
  onCandidateChange,
  onResolutionChange,
  onGrantDecisionChange,
}: EmployeeImportReviewGridProps) {
  const gridApi = useRef<any>(null);
  const fieldNames = useMemo(() => {
    const fields: string[] = [];
    allRows.forEach((row) => {
      row.validationData.forEach((item) => {
        if (
          !HIDDEN_REVIEW_FIELDS.has(item.name) &&
          item.name !== "name" &&
          !fields.includes(item.name)
        ) {
          fields.push(item.name);
        }
      });
    });
    const nomineeFields = new Set(NOMINEE_PREFERRED_ORDER);
    const firstNomineeIndex = fields.findIndex((field) =>
      nomineeFields.has(field)
    );
    const nonNomineeFields = fields.filter(
      (field) => !nomineeFields.has(field)
    );
    const orderedNomineeFields = NOMINEE_PREFERRED_ORDER.filter((field) =>
      fields.includes(field)
    );
    if (firstNomineeIndex < 0) return nonNomineeFields;
    const insertAt = fields
      .slice(0, firstNomineeIndex)
      .filter((field) => !nomineeFields.has(field)).length;
    return [
      ...nonNomineeFields.slice(0, insertAt),
      ...orderedNomineeFields,
      ...nonNomineeFields.slice(insertAt),
    ];
  }, [allRows]);

  const cellEditorSelector = (
    params: ICellEditorParams<any>
  ): CellEditorSelectorResult | undefined => {
    switch (params.colDef.field) {
      case "employmentStatus":
        return {
          component: "agRichSelectCellEditor",
          params: { values: ["EMPLOYED", "RESIGNED"] },
        };
      case "employmentType":
        return {
          component: "agRichSelectCellEditor",
          params: { values: ["EMPLOYEE", "CONSULTANT"] },
        };
      case "relationship1":
      case "relationship2":
      case "guardianRelationship1":
      case "guardianRelationship2":
        return {
          component: "agRichSelectCellEditor",
          params: { values: NOMINEE_RELATIONSHIP_OPTIONS },
        };
      default:
        return DATE_FIELDS.has(String(params.colDef.field))
          ? { component: "agDateCellEditor" }
          : undefined;
    }
  };

  const saveCellValue = (params: ValueSetterParams) => {
    if (params.newValue === undefined || params.newValue === null) return false;
    const field = String(params.colDef.field || "");
    const nextRow = updateRowField(params.data, field, String(params.newValue));
    Object.assign(params.data, nextRow);
    onEdit(nextRow);
    return true;
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Validity",
        field: "__status",
        pinned: "left",
        width: 220,
        checkboxSelection: (params: any) =>
          isEmployeeImportSelectable(params.data),
        headerCheckboxSelection: true,
        headerCheckboxSelectionCurrentPageOnly: true,
        valueGetter: (params: any) => statusLabel(params.data.status),
        cellRenderer: (params: any) => <StatusCell data={params.data} />,
        editable: false,
        filter: false,
        sortable: false,
        resizable: false,
        suppressColumnsToolPanel: true,
        menuTabs: [],
      },
      {
        headerName: "Employee name",
        field: "__employeeName",
        pinned: "left",
        minWidth: 180,
        valueGetter: (params: any) => getEmployeeName(params.data),
        valueSetter: saveCellValue,
        cellRenderer: (params: any) => (
          <EditableCell
            value={params.value}
            data={params.data}
            colDef={params.colDef}
          />
        ),
      },
      ...(showBoomerangColumns
        ? [
            {
              headerName: "Former employment match",
              field: "__formerEmployment",
              minWidth: 380,
              editable: (params: any) => params.data.candidates.length > 0,
              valueGetter: (params: any) =>
                getSelectedBoomerangCandidate(params.data)?.employeeId || "",
              valueFormatter: (params: any) => {
                const candidate = params.data.candidates.find(
                  (item: BoomerangCandidate) => item.employeeId === params.value
                );
                return candidate
                  ? formatBoomerangCandidate(candidate)
                  : `Select from ${params.data.candidates.length} match${
                      params.data.candidates.length === 1 ? "" : "es"
                    }`;
              },
              cellRenderer: (params: any) => {
                const candidate = params.data.candidates.find(
                  (item: BoomerangCandidate) => item.employeeId === params.value
                );
                if (!candidate) {
                  return (
                    <span className="text-secondaryText">
                      Select from {params.data.candidates.length} match
                      {params.data.candidates.length === 1 ? "" : "es"}
                    </span>
                  );
                }
                return (
                  <VStack className="min-w-0 justify-center gap-0.5">
                    <span className="truncate font-medium text-headerColor">
                      {candidate.employeeName}
                      <span className="font-normal text-secondaryText">
                        {" "}
                        · {candidate.employeeIdentification}
                      </span>
                    </span>
                    <span className="truncate text-xs text-secondaryText">
                      Matched by: {formatBoomerangMatchingCriteria(candidate)}
                    </span>
                  </VStack>
                );
              },
              tooltipValueGetter: (params: any) => {
                const candidate = params.data.candidates.find(
                  (item: BoomerangCandidate) => item.employeeId === params.value
                );
                return candidate ? formatBoomerangCandidate(candidate) : "";
              },
              valueSetter: (params: any) => {
                if (params.newValue === params.value) return false;
                onCandidateChange(params.data, params.newValue);
                return true;
              },
              cellEditorSelector: (params: any) => ({
                component: "agRichSelectCellEditor",
                params: {
                  values: params.data.candidates.map(
                    (candidate: BoomerangCandidate) => candidate.employeeId
                  ),
                  formatValue: (candidateId: string) => {
                    const candidate = params.data.candidates.find(
                      (item: BoomerangCandidate) =>
                        item.employeeId === candidateId
                    );
                    return candidate ? formatBoomerangCandidate(candidate) : "";
                  },
                },
                popup: false,
              }),
            },
            {
              headerName: "How to resolve",
              field: "__resolutionStrategy",
              minWidth: 210,
              editable: (params: any) =>
                Boolean(getSelectedBoomerangCandidate(params.data)),
              filter: false,
              sortable: false,
              valueGetter: (params: any) =>
                params.data.resolution?.strategy || "",
              valueFormatter: (params: any) =>
                RESOLUTION_STRATEGY_OPTIONS.find(
                  (option) => option.value === params.value
                )?.label || "Choose resolution",
              valueSetter: (params: any) => {
                if (params.newValue === params.value) return false;
                onResolutionChange(
                  params.data,
                  params.newValue as BoomerangResolutionStrategy
                );
                return true;
              },
              cellEditor: "agRichSelectCellEditor",
              cellEditorParams: {
                values: RESOLUTION_STRATEGY_OPTIONS.map(
                  (option) => option.value
                ),
                formatValue: (strategy: BoomerangResolutionStrategy) =>
                  RESOLUTION_STRATEGY_OPTIONS.find(
                    (option) => option.value === strategy
                  )?.label || "",
              },
              cellEditorPopup: false,
            },
            {
              headerName: "Move grants",
              field: "__moveGrants",
              minWidth: 130,
              editable: (params: any) =>
                params.data.resolution?.strategy === "CREATE_SEPARATE_EMPLOYEE",
              filter: false,
              sortable: false,
              valueGetter: (params: any) =>
                params.data.resolution?.strategy === "CREATE_SEPARATE_EMPLOYEE"
                  ? params.data.resolution.moveGrants
                    ? "YES"
                    : "NO"
                  : "",
              valueFormatter: (params: any) =>
                params.value === "YES"
                  ? "Yes"
                  : params.value === "NO"
                  ? "No"
                  : "-",
              valueSetter: (params: any) => {
                if (params.newValue === params.value) return false;
                onGrantDecisionChange(
                  params.data,
                  "moveGrants",
                  params.newValue === "YES"
                );
                return true;
              },
              cellEditor: "agRichSelectCellEditor",
              cellEditorParams: {
                values: ["YES", "NO"],
                formatValue: (value: string) =>
                  value === "YES" ? "Yes" : "No",
              },
              cellEditorPopup: false,
            },
            {
              headerName: "Continue vesting",
              field: "__continueVesting",
              minWidth: 150,
              editable: (params: any) => {
                const resolution = params.data.resolution as
                  | BoomerangResolution
                  | undefined;
                return (
                  resolution?.strategy === "MERGE_EXISTING_EMPLOYEE" ||
                  (resolution?.strategy === "CREATE_SEPARATE_EMPLOYEE" &&
                    resolution.moveGrants)
                );
              },
              filter: false,
              sortable: false,
              valueGetter: (params: any) => {
                const resolution = params.data.resolution as
                  | BoomerangResolution
                  | undefined;
                if (
                  resolution?.strategy !== "MERGE_EXISTING_EMPLOYEE" &&
                  resolution?.strategy !== "CREATE_SEPARATE_EMPLOYEE"
                ) {
                  return "";
                }
                if (
                  resolution.strategy === "CREATE_SEPARATE_EMPLOYEE" &&
                  !resolution.moveGrants
                ) {
                  return "";
                }
                return resolution.continueVesting ? "YES" : "NO";
              },
              valueFormatter: (params: any) =>
                params.value === "YES"
                  ? "Yes"
                  : params.value === "NO"
                  ? "No"
                  : "-",
              valueSetter: (params: any) => {
                if (params.newValue === params.value) return false;
                onGrantDecisionChange(
                  params.data,
                  "continueVesting",
                  params.newValue === "YES"
                );
                return true;
              },
              cellEditor: "agRichSelectCellEditor",
              cellEditorParams: {
                values: ["YES", "NO"],
                formatValue: (value: string) =>
                  value === "YES" ? "Yes" : "No",
              },
              cellEditorPopup: false,
            },
          ]
        : []),
      ...fieldNames.map(
        (field): ColDef => ({
          headerName: field.startsWith("udf:")
            ? udfFieldLabels[field] || formatFieldName(field.slice(4))
            : formatFieldName(field),
          field,
          minWidth: 150,
          valueGetter: (params: any) => getFieldValue(params.data, field),
          valueSetter: saveCellValue,
          cellEditorSelector,
          cellRenderer: (params: any) => (
            <EditableCell
              value={params.value}
              data={params.data}
              colDef={params.colDef}
            />
          ),
        })
      ),
    ],
    [
      fieldNames,
      onCandidateChange,
      onGrantDecisionChange,
      onResolutionChange,
      showBoomerangColumns,
      udfFieldLabels,
    ]
  );

  useEffect(() => {
    if (!gridApi.current) return;
    const selected = new Set(selectedRowIds);
    gridApi.current.forEachNode((node: any) => {
      node.setSelected(selected.has(node.data.rowId));
    });
  }, [rows, selectedRowIds]);

  return (
    <div className="bulk-state-grid ag-theme-material h-[560px] w-full">
      <AgGridReact
        rowData={rows}
        columnDefs={columnDefs}
        getRowId={(params) => params.data.rowId}
        defaultColDef={{
          editable: true,
          filter: true,
          resizable: true,
          sortable: true,
          minWidth: 150,
          menuTabs: ["filterMenuTab"],
          wrapHeaderText: true,
          autoHeaderHeight: true,
        }}
        rowSelection="multiple"
        isRowSelectable={(node) =>
          Boolean(node.data && isEmployeeImportSelectable(node.data))
        }
        suppressRowClickSelection
        suppressCellFocus
        pagination
        alwaysShowHorizontalScroll
        stopEditingWhenCellsLoseFocus
        sideBar={{
          toolPanels: [
            {
              id: "columns",
              labelDefault: "Columns",
              labelKey: "columns",
              iconKey: "columns",
              toolPanel: "agColumnsToolPanel",
              toolPanelParams: {
                suppressRowGroups: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressValues: true,
              },
            },
            {
              id: "filters",
              labelDefault: "Filters",
              labelKey: "filters",
              iconKey: "filter",
              toolPanel: "agFiltersToolPanel",
            },
          ],
        }}
        onGridReady={(params) => {
          gridApi.current = params.api;
          const selected = new Set(selectedRowIds);
          params.api.forEachNode((node: any) => {
            node.setSelected(selected.has(node.data.rowId));
          });
        }}
        onSelectionChanged={(params) => {
          const visibleRowIds = new Set(rows.map((row) => row.rowId));
          const selectedVisibleIds = params.api
            .getSelectedRows()
            .filter(isEmployeeImportSelectable)
            .map((row: BulkEmployeeImportRow) => row.rowId);
          onSelectionChange([
            ...selectedRowIds.filter((rowId) => !visibleRowIds.has(rowId)),
            ...selectedVisibleIds,
          ]);
        }}
        overlayNoRowsTemplate="<span>No rows in this view</span>"
      />
    </div>
  );
}

export default function EmployeeCreationImportPage() {
  const navigate = useNavigate();
  const { data: savedConfigs = [] } = useGetEmployeeUploadConfigs();
  const [importSource, setImportSource] = useState<ImportSource>();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File>();
  const [upload, setUpload] = useState<EmployeeUploadHeaderResponse>();
  const [mapping, setMapping] = useState<Map<string, string>>(new Map());
  const [mapName, setMapName] = useState("");
  const [selectedMapId, setSelectedMapId] = useState("");
  const [review, setReview] = useState<BulkEmployeeImportValidationResult>();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<ReviewTab>("ALL");
  const [isBulkResolutionOpen, setIsBulkResolutionOpen] = useState(false);
  const [result, setResult] = useState<BulkEmployeeImportCommitResult>();
  const [loading, setLoading] = useState(false);
  const [reviewAction, setReviewAction] = useState<
    "VALIDATING_ROWS" | "CREATING_EMPLOYEES"
  >();
  const [downloading, setDownloading] = useState(false);
  const [hasPendingEdits, setHasPendingEdits] = useState(false);
  const latestReviewRequest = useRef(0);

  const returnToSourceSelection = () => {
    latestReviewRequest.current += 1;
    setImportSource(undefined);
    setStep(1);
    setFile(undefined);
    setUpload(undefined);
    setMapping(new Map());
    setMapName("");
    setSelectedMapId("");
    setReview(undefined);
    setSelectedRowIds([]);
    setActiveTab("ALL");
    setIsBulkResolutionOpen(false);
    setResult(undefined);
    setReviewAction(undefined);
    setHasPendingEdits(false);
  };

  const loadFile = async (nextFile: File) => {
    setLoading(true);
    try {
      const base64 = await convertToBase64(nextFile);
      const response = await addEmplooyeesViaExcel({
        file: base64,
        headerRow: 1,
      } as any);
      setFile(nextFile);
      setUpload(response);
      setSelectedMapId("");
      setMapping(getPrefilledMapping(response));
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorMessage ||
          "We couldn't read that Excel file"
      );
    } finally {
      setLoading(false);
    }
  };

  const validate = async () => {
    if (!upload?.cacheId) return;
    setLoading(true);
    try {
      const mapperData: Record<string, { type: string; field: string }> = {};
      mapping.forEach((field, header) => {
        if (field && field !== "--IGNORE--")
          mapperData[field] = { type: "direct", field: header };
      });
      const validationPayload = {
        cacheId: upload.cacheId,
        headerRow: 1,
        mapperData,
        ...(mapName.trim() ? { name: mapName.trim() } : {}),
      };
      let response: BulkEmployeeImportValidationResult;
      try {
        response = await validateBulkEmployeeImport(validationPayload);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.errorMessage;
        if (!file || errorMessage !== EXCEL_REUPLOAD_REQUIRED_MESSAGE)
          throw error;
        response = await validateBulkEmployeeImport({
          ...validationPayload,
          file: await convertToBase64(file),
        });
      }
      const rows = applySingleCandidateDefaults(response.rows);
      const hasDefaultedBoomerangs = rows.some(
        (row, index) => row !== response.rows[index]
      );
      setReview({
        ...response,
        rows,
        summary: getEmployeeImportReviewSummary(rows),
      });
      setSelectedRowIds(
        rows.filter(isEmployeeImportCommitEligible).map((row) => row.rowId)
      );
      setHasPendingEdits(hasDefaultedBoomerangs);
      setStep(2);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorMessage ||
          "We couldn't validate this import"
      );
    } finally {
      setLoading(false);
    }
  };

  const refreshEditedRows = async (rows: BulkEmployeeImportRow[]) => {
    const requestId = ++latestReviewRequest.current;
    setReviewAction("VALIDATING_ROWS");
    setLoading(true);
    try {
      const response = await validateBulkEmployeeImport({
        rows: rows.map((row) => ({
          employee: {
            ...toEmployeeImportInput(row.employee),
            ...(row.resolution ? { boomerangResolution: row.resolution } : {}),
          },
        })),
      });
      if (requestId !== latestReviewRequest.current) return;
      const refreshedRows = applySingleCandidateDefaults(response.rows);
      const hasDefaultedBoomerangs = refreshedRows.some(
        (row, index) => row !== response.rows[index]
      );
      setReview({
        ...response,
        rows: refreshedRows,
        summary: getEmployeeImportReviewSummary(refreshedRows),
      });
      setSelectedRowIds((current) =>
        current.filter((id) =>
          refreshedRows.find(
            (row) => row.rowId === id && isEmployeeImportCommitEligible(row)
          )
        )
      );
      setHasPendingEdits(hasDefaultedBoomerangs);
    } catch {
      if (requestId === latestReviewRequest.current)
        toast.error("We couldn't revalidate the edited rows");
    } finally {
      if (requestId === latestReviewRequest.current) {
        setLoading(false);
        setReviewAction(undefined);
      }
    }
  };

  const updateBoomerangCandidate = (
    changedRow: BulkEmployeeImportRow,
    candidateId: string
  ) => {
    if (!review) return;
    const rows = review.rows.map((row) => {
      if (row.rowId !== changedRow.rowId) return row;
      const candidate = row.candidates.find(
        (item) => item.employeeId === candidateId
      );
      if (!candidate) {
        return {
          ...row,
          selectedCandidateId: undefined,
          resolution: undefined,
          status: "BOOMERANG_CANDIDATE" as const,
        };
      }
      return {
        ...row,
        selectedCandidateId: candidate.employeeId,
        resolution: undefined,
        status: "BOOMERANG_CANDIDATE" as const,
      };
    });
    setReview({
      ...review,
      rows,
      summary: getEmployeeImportReviewSummary(rows),
    });
    setHasPendingEdits(true);
  };

  const updateBoomerangResolution = (
    changedRow: BulkEmployeeImportRow,
    strategy: BoomerangResolutionStrategy
  ) => {
    if (!review) return;
    if (
      !RESOLUTION_STRATEGY_OPTIONS.some((option) => option.value === strategy)
    ) {
      return;
    }
    const candidate = getSelectedBoomerangCandidate(changedRow);
    if (!candidate) return;
    if (strategy === "CREATE_SEPARATE_EMPLOYEE" && candidate.alreadyLinked) {
      toast.error(
        "Link and create is unavailable because this former employment is already linked."
      );
      return;
    }
    const rows = review.rows.map((row) =>
      row.rowId === changedRow.rowId
        ? {
            ...row,
            resolution: createBoomerangResolution(candidate, strategy, {
              reason: row.resolution?.reason,
            }),
            status: "RESOLVED_BOOMERANG" as const,
          }
        : row
    );
    setReview({
      ...review,
      rows,
      summary: getEmployeeImportReviewSummary(rows),
    });
    setHasPendingEdits(true);
  };

  const updateBoomerangGrantDecision = (
    changedRow: BulkEmployeeImportRow,
    decision: "moveGrants" | "continueVesting",
    value: boolean
  ) => {
    if (!review || !changedRow.resolution) return;
    const currentResolution = changedRow.resolution;
    let resolution: BoomerangResolution;

    if (decision === "moveGrants") {
      if (currentResolution.strategy !== "CREATE_SEPARATE_EMPLOYEE") return;
      resolution = {
        ...currentResolution,
        moveGrants: value,
        continueVesting: value ? currentResolution.continueVesting : false,
      };
    } else if (currentResolution.strategy === "MERGE_EXISTING_EMPLOYEE") {
      resolution = { ...currentResolution, continueVesting: value };
    } else if (
      currentResolution.strategy === "CREATE_SEPARATE_EMPLOYEE" &&
      currentResolution.moveGrants
    ) {
      resolution = { ...currentResolution, continueVesting: value };
    } else {
      return;
    }

    const rows = review.rows.map((row) =>
      row.rowId === changedRow.rowId
        ? {
            ...row,
            resolution,
            status: "RESOLVED_BOOMERANG" as const,
          }
        : row
    );
    setReview({
      ...review,
      rows,
      summary: getEmployeeImportReviewSummary(rows),
    });
    setHasPendingEdits(true);
  };

  const applyBulkResolution = (choice: BulkResolutionChoice) => {
    if (!review) return;
    const selectedIds = new Set(selectedRowIds);
    const rows = review.rows.map((row) => {
      if (!selectedIds.has(row.rowId) || !row.candidates.length) return row;
      const candidate = getSelectedBoomerangCandidate(row);
      if (!candidate) return row;
      return {
        ...row,
        selectedCandidateId: candidate.employeeId,
        resolution: createBoomerangResolution(candidate, choice.strategy, {
          ...choice,
          reason: choice.reason || row.resolution?.reason,
        }),
        status: "RESOLVED_BOOMERANG" as const,
      };
    });
    setReview({
      ...review,
      rows,
      summary: getEmployeeImportReviewSummary(rows),
    });
    setIsBulkResolutionOpen(false);
    setHasPendingEdits(true);
  };

  const commit = async () => {
    if (!review) return;
    setReviewAction("CREATING_EMPLOYEES");
    setLoading(true);
    try {
      const rows = review.rows
        .filter((row) => selectedRowIds.includes(row.rowId))
        .map((row) => ({
          rowId: row.rowId,
          employee: toEmployeeImportInput(row.employee),
          resolution: row.resolution,
        }));
      const response = await commitBulkEmployeeImport(rows);
      setResult(response);
      setStep(3);
    } catch (error: any) {
      const rows = error?.response?.data?.errorRaw?.rows;
      if (rows) {
        setReview({ rows, summary: getEmployeeImportReviewSummary(rows) });
        setSelectedRowIds((current) =>
          current.filter((rowId) =>
            rows.some(
              (row: BulkEmployeeImportRow) =>
                row.rowId === rowId && isEmployeeImportCommitEligible(row)
            )
          )
        );
        setActiveTab("INVALID");
      }
      toast.error(
        error?.response?.data?.errorMessage ||
          "We couldn't import the selected employees"
      );
    } finally {
      setLoading(false);
      setReviewAction(undefined);
    }
  };

  const visibleRows = useMemo(
    () => getEmployeeImportRowsForTab(review?.rows || [], activeTab),
    [review, activeTab]
  );

  const reviewSummary = useMemo(
    () => getEmployeeImportReviewSummary(review?.rows || []),
    [review?.rows]
  );

  const selectedBoomerangRows = useMemo(
    () =>
      (review?.rows || []).filter(
        (row) => selectedRowIds.includes(row.rowId) && row.candidates.length > 0
      ),
    [review?.rows, selectedRowIds]
  );

  const hasUnselectedBoomerangMatch = selectedBoomerangRows.some(
    (row) => !getSelectedBoomerangCandidate(row)
  );

  const hasRowsRequiringValidation = useMemo(
    () =>
      (review?.rows || []).some((row) => !isEmployeeImportCommitEligible(row)),
    [review?.rows]
  );

  const proceedDisabledReason =
    hasPendingEdits || hasRowsRequiringValidation
      ? "Validate all rows again"
      : !selectedRowIds.length
      ? "Select at least one validated row"
      : undefined;

  useEffect(() => {
    if (activeTab === "BOOMERANG" && !reviewSummary.boomerangCandidates) {
      setActiveTab("ALL");
    }
  }, [activeTab, reviewSummary.boomerangCandidates]);

  const updateEditedRow = (nextRow: BulkEmployeeImportRow) => {
    if (!review) return;
    latestReviewRequest.current += 1;
    const rows = review.rows.map((row) =>
      row.rowId === nextRow.rowId ? nextRow : row
    );
    setReview({
      ...review,
      rows,
      summary: getEmployeeImportReviewSummary(rows),
    });
    setHasPendingEdits(true);
  };

  return (
    <main className="w-full">
      <VStack className="w-full gap-6">
        <BCHeader
          className="items-baseline"
          bcTitle="Stakeholders"
          bcSubTitle="Import employees"
          onClick={() => navigate("/options/allEmployees")}
        />
        <HStack className="w-full flex-col items-start justify-between gap-4 sm:flex-row">
          <VStack className="gap-1">
            <h1 className="text-xl font-semibold leading-7 text-headerColor">
              {importSource === "EXCEL"
                ? "Import employees from Excel"
                : "Import employees"}
            </h1>
            <p className="text-sm text-secondaryText">
              {importSource === "EXCEL"
                ? "Validate employee data and resolve former employment matches before importing."
                : "Choose a source to import employee records."}
            </p>
          </VStack>
          {importSource && (
            <HStack className="shrink-0 flex-wrap items-center gap-3 pt-1">
              <TertiaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                onClick={returnToSourceSelection}
              >
                Back to import sources
              </TertiaryCTAButton>
              {importSource === "EXCEL" && step === 1 && (
                <PrimaryCTAButton
                  buttonSize={ButtonSize.MEDIUM}
                  loading={downloading}
                  onClick={async () => {
                    setDownloading(true);
                    try {
                      downloadBlobObject(
                        await getEmployeeTemplate(),
                        "Employee_Upload_Template.xlsx"
                      );
                    } catch {
                      toast.error("We couldn't download the template");
                    } finally {
                      setDownloading(false);
                    }
                  }}
                >
                  <Icon
                    icon="mdi:download-outline"
                    width={18}
                    className="mr-2"
                  />
                  Download template
                </PrimaryCTAButton>
              )}
            </HStack>
          )}
        </HStack>
        {!importSource && (
          <section aria-label="Import sources">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {IMPORT_SOURCES.map((source) => (
                <Card
                  key={source.id}
                  className="flex h-full flex-col border-borderColor"
                >
                  <CardHeader className="items-center gap-4 text-center">
                    <img
                      src={source.logo}
                      alt=""
                      aria-hidden="true"
                      className="h-16 w-40 object-contain"
                    />
                    <CardTitle className="text-base leading-6 text-headerColor">
                      {source.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <p className="text-center text-sm text-secondaryText">
                      {source.description}
                    </p>
                  </CardContent>
                  <CardFooter className="justify-center !px-6 !pb-6">
                    <SecondaryCTAButton
                      buttonSize={ButtonSize.MEDIUM}
                      onClick={() => setImportSource(source.id)}
                    >
                      {source.actionLabel}
                    </SecondaryCTAButton>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </section>
        )}
        {importSource === "GREYTIP" && (
          <GreyTipImport resetData={returnToSourceSelection} />
        )}
        {importSource === "DARWINBOX" && (
          <DarwinBoxDatasetImport resetData={returnToSourceSelection} />
        )}
        {importSource === "EXCEL" && step === 1 && (
          <Card className="flex min-h-[600px] flex-col border-borderColor bg-white">
            <CardContent className="flex min-h-[440px] flex-1 flex-col !px-8 !py-6">
              {!upload ? (
                <VStack className="m-auto w-full max-w-2xl gap-6">
                  <VStack className="gap-1 text-center">
                    <h2 className="text-xl font-medium text-primaryText">
                      Upload file
                    </h2>
                    <p className="text-sm text-secondaryText">
                      Please ensure your file follows the template format before
                      uploading.
                    </p>
                  </VStack>
                  <VStack className="gap-3">
                    <FileDropzone
                      selectedFile={file}
                      onFileSelect={loadFile}
                      onClear={() => {
                        setFile(undefined);
                        setUpload(undefined);
                        setMapping(new Map());
                        setSelectedMapId("");
                      }}
                      instructionText="Excel files only (.xlsx, .xls)"
                    />
                    <p className="text-center text-xs italic text-secondaryText">
                      Important: Please upload Excel sheets with no empty rows
                      before the header row for accurate data processing.
                    </p>
                  </VStack>
                  <Instructions
                    data={[
                      "Use the downloaded template to ensure correct format",
                      "Upload only .xlsx or .xls files",
                      "You can map different Excel headers in the next step",
                      "You can provide up to two demat account IDs for each employee",
                      "Separate multiple employee tags with commas",
                      "Nominee contact and address are required when nominee details are provided",
                    ]}
                  />
                </VStack>
              ) : (
                <VStack className="py-8">
                  <HStack className="mx-auto items-center gap-40">
                    <VStack className="min-w-96 align-center">
                      <Label className="inline-flex gap-4">
                        Use an existing map
                        <Tooltip
                          text="Apply a previously saved column mapping to this Excel file."
                          _className="w-[400px]"
                        >
                          <Icon
                            icon="material-symbols:info-outline-rounded"
                            height={24}
                            width={24}
                            aria-hidden="true"
                            className="w-full cursor-pointer rounded text-xs font-medium text-slate-dark"
                          />
                        </Tooltip>
                      </Label>
                      <Select
                        options={savedConfigs}
                        name=""
                        disabled={savedConfigs.length === 0}
                        textGetter={(config) => config.name || config.id}
                        valueGetter={(config) => config.id}
                        onChange={(event) => {
                          const config = savedConfigs.find(
                            (item) => item.id === event.target.value
                          );
                          if (!config) return;
                          const selectedConfig = config as ImportConfig;
                          setSelectedMapId(selectedConfig.id);
                          const next = new Map<string, string>();
                          Object.entries(
                            selectedConfig.mapperData || {}
                          ).forEach(([field, mappingConfig]) =>
                            next.set(mappingConfig.field, field)
                          );
                          setMapping(next);
                        }}
                      />
                    </VStack>
                    <VStack className="min-w-96 align-center">
                      <Label
                        htmlFor="employee-import-map-name"
                        className="inline-flex gap-4 text-sm font-normal"
                      >
                        Config template name
                        <Tooltip
                          text="Save the current column mapping with a reusable name."
                          _className="w-[400px]"
                        >
                          <Icon
                            icon="material-symbols:info-outline-rounded"
                            height={24}
                            width={24}
                            aria-hidden="true"
                            className="w-full cursor-pointer rounded text-xs font-medium text-slate-dark"
                          />
                        </Tooltip>
                      </Label>
                      <Input
                        id="employee-import-map-name"
                        value={mapName}
                        onChange={(event) => setMapName(event.target.value)}
                      />
                    </VStack>
                  </HStack>
                  <HStack className="mx-10 gap-8 pt-14">
                    <table className="w-full flex-1">
                      <thead>
                        <tr>
                          <th>
                            <HStack className="items-center gap-2">
                              Excel header
                              <Tooltip
                                text="Column headers detected in your uploaded Excel file."
                                _className="w-[400px] font-normal"
                              >
                                <Icon
                                  icon="material-symbols:info-outline-rounded"
                                  height={24}
                                  width={24}
                                  aria-hidden="true"
                                  className="w-full cursor-pointer rounded text-xs font-medium text-slate-dark"
                                />
                              </Tooltip>
                            </HStack>
                          </th>
                          <th>
                            <HStack className="items-center gap-2">
                              Hissa field
                              <Tooltip
                                text="Choose the Hissa employee field that should receive data from this Excel column."
                                _className="w-[400px] font-normal"
                              >
                                <Icon
                                  icon="material-symbols:info-outline-rounded"
                                  height={24}
                                  width={24}
                                  aria-hidden="true"
                                  className="w-full cursor-pointer rounded text-xs font-medium text-slate-dark"
                                />
                              </Tooltip>
                            </HStack>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {upload.headersInFile.map((header) => (
                          <tr key={header}>
                            <td className="w-2/5 text-sm font-normal">
                              <Box>
                                <p>{header}</p>
                                <p className="text-xxs text-slate-dark">
                                  eg: (
                                  {formatExcelSampleValue(
                                    header,
                                    upload.sampleData?.[header],
                                    mapping.get(header)
                                  )}
                                  )
                                </p>
                              </Box>
                            </td>
                            <td className="w-2/5 p-2 text-sm font-normal">
                              <SearchDropDown
                                options={[
                                  "--IGNORE--",
                                  ...upload.headersInSystem.filter(
                                    (field) => field !== "companyId"
                                  ),
                                ]}
                                value={mapping.get(header) || ""}
                                placeholder="-- Select Field --"
                                className=""
                                getOptionLabel={(field) =>
                                  SYSTEM_FIELD_LABELS[field] ||
                                  upload.udfFieldLabels?.[field] ||
                                  formatFieldName(field)
                                }
                                isOptionEqualToValue={(option, value) =>
                                  (option || "") === (value || "")
                                }
                                onChange={(_, value) => {
                                  const next = new Map(mapping);
                                  next.set(header, value || "");
                                  setMapping(next);
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </HStack>
                </VStack>
              )}
            </CardContent>
            {upload && (
              <CardFooter className="w-full justify-end gap-3 border-t border-borderColor !p-4">
                <PrimaryCTAButton
                  buttonSize={ButtonSize.MEDIUM}
                  disabled={!upload.cacheId || loading}
                  onClick={validate}
                >
                  {loading ? "Validating..." : "Continue"}
                </PrimaryCTAButton>
              </CardFooter>
            )}
          </Card>
        )}
        {importSource === "EXCEL" && step === 2 && review && (
          <>
            <section>
              <HStack className="flex-wrap items-stretch gap-4">
                {[
                  ["Total rows parsed", reviewSummary.totalRows],
                  ["Ready to import", reviewSummary.commitEligibleRows],
                  ["Invalid rows", reviewSummary.errorRows],
                  ["Boomerang cases", reviewSummary.boomerangCandidates],
                ].map(([label, count]) => (
                  <SummaryMetricCard
                    key={String(label)}
                    label={String(label)}
                    value={count}
                  />
                ))}
              </HStack>
            </section>
            <Card className="border-borderColor">
              <CardHeader className="gap-4">
                <CardTitle className="text-base leading-6 text-headerColor">
                  Validate and resolve
                </CardTitle>
                <HStack className="w-full flex-wrap items-center justify-between gap-3">
                  <HStack className="flex-wrap gap-4">
                    {[
                      ["ALL", `All rows (${reviewSummary.totalRows})`],
                      ["INVALID", `Invalid rows (${reviewSummary.errorRows})`],
                      ...(reviewSummary.boomerangCandidates
                        ? [
                            [
                              "BOOMERANG",
                              `Boomerang (${reviewSummary.boomerangCandidates})`,
                            ],
                          ]
                        : []),
                      [
                        "READY",
                        `Ready to import (${reviewSummary.commitEligibleRows})`,
                      ],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setActiveTab(id as ReviewTab)}
                        className={`border-b-2 pb-2 text-xs2 font-medium ${
                          activeTab === id
                            ? "border-primary text-primary"
                            : "border-transparent text-secondaryText"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </HStack>
                  {activeTab === "BOOMERANG" &&
                    reviewSummary.boomerangCandidates > 0 && (
                      <SecondaryCTAButton
                        buttonSize={ButtonSize.SMALL}
                        disabled={
                          !selectedBoomerangRows.length ||
                          hasUnselectedBoomerangMatch
                        }
                        aria-label={
                          !selectedBoomerangRows.length
                            ? "Select one or more boomerang rows to use Bulk action"
                            : hasUnselectedBoomerangMatch
                            ? "Select a former employment for every selected boomerang row"
                            : undefined
                        }
                        onClick={() => setIsBulkResolutionOpen(true)}
                      >
                        {`Bulk action (${selectedBoomerangRows.length})`}
                      </SecondaryCTAButton>
                    )}
                </HStack>
                {reviewSummary.boomerangCandidates > 0 && (
                  <Box className="border-l-4 border-primary bg-orange-50 p-4">
                    <p className="text-sm font-medium text-headerColor">
                      Review boomerang cases
                    </p>
                    <p className="mt-1 text-sm text-secondaryText">
                      We found {reviewSummary.boomerangCandidates} Boomerang
                      case
                      {reviewSummary.boomerangCandidates === 1 ? "" : "s"}. In
                      the Boomerang tab, select the cases and their former
                      employments, then use Bulk action to apply a rejoin
                      decision.
                    </p>
                  </Box>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {reviewSummary.errorRows > 1 && (
                  <HStack
                    role="alert"
                    className="items-start gap-2 border-b border-red-200 bg-red-50 px-6 py-3"
                  >
                    <Icon
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-red-700"
                      icon="lucide:circle-alert"
                      width={18}
                    />
                    <p className="text-sm text-red-700">
                      {reviewSummary.errorRows} rows have validation errors that
                      need attention. Review the Invalid rows tab and correct
                      them before importing.
                    </p>
                  </HStack>
                )}
                <EmployeeImportReviewGrid
                  rows={visibleRows}
                  allRows={review.rows}
                  showBoomerangColumns={activeTab === "BOOMERANG"}
                  selectedRowIds={selectedRowIds}
                  udfFieldLabels={upload?.udfFieldLabels || {}}
                  onSelectionChange={setSelectedRowIds}
                  onEdit={updateEditedRow}
                  onCandidateChange={updateBoomerangCandidate}
                  onResolutionChange={updateBoomerangResolution}
                  onGrantDecisionChange={updateBoomerangGrantDecision}
                />
              </CardContent>
              <CardFooter className="w-full border-t border-borderColor !p-4">
                <VStack className="w-full gap-3">
                  {hasPendingEdits && (
                    <p className="text-sm text-secondaryText">
                      Validate all rows to refresh errors and employment matches
                      before importing.
                    </p>
                  )}
                  <HStack className="w-full flex-wrap items-center justify-between gap-3">
                    <TertiaryCTAButton
                      buttonSize={ButtonSize.MEDIUM}
                      onClick={() => setStep(1)}
                    >
                      Back
                    </TertiaryCTAButton>
                    <HStack className="flex-wrap items-center justify-end gap-3">
                      <SecondaryCTAButton
                        buttonSize={ButtonSize.MEDIUM}
                        disabled={loading}
                        aria-label={
                          reviewAction === "VALIDATING_ROWS"
                            ? "Validating all rows"
                            : undefined
                        }
                        onClick={() => refreshEditedRows(review.rows)}
                      >
                        {reviewAction === "VALIDATING_ROWS" ? (
                          <Icon
                            aria-hidden="true"
                            className="animate-spin"
                            icon="lucide:loader-2"
                            width={18}
                          />
                        ) : (
                          "Validate all rows"
                        )}
                      </SecondaryCTAButton>
                      {proceedDisabledReason ? (
                        <Tooltip
                          text={proceedDisabledReason}
                          className="!bottom-full !left-auto !right-0 z-50 w-max"
                          _className="w-max whitespace-nowrap"
                        >
                          <span className="inline-flex cursor-not-allowed">
                            <PrimaryCTAButton
                              buttonSize={ButtonSize.MEDIUM}
                              disabled
                              aria-label={proceedDisabledReason}
                              onClick={commit}
                            >
                              {`Proceed to create (${selectedRowIds.length} rows)`}
                            </PrimaryCTAButton>
                          </span>
                        </Tooltip>
                      ) : (
                        <PrimaryCTAButton
                          buttonSize={ButtonSize.MEDIUM}
                          disabled={loading}
                          aria-label={
                            reviewAction === "CREATING_EMPLOYEES"
                              ? "Creating employees"
                              : undefined
                          }
                          onClick={commit}
                        >
                          {reviewAction === "CREATING_EMPLOYEES" ? (
                            <Icon
                              aria-hidden="true"
                              className="animate-spin"
                              icon="lucide:loader-2"
                              width={18}
                            />
                          ) : (
                            `Proceed to create (${selectedRowIds.length} rows)`
                          )}
                        </PrimaryCTAButton>
                      )}
                    </HStack>
                  </HStack>
                </VStack>
              </CardFooter>
            </Card>
          </>
        )}
        {importSource === "EXCEL" && step === 3 && result && (
          <Card className="border-borderColor">
            <CardHeader>
              <CardTitle className="text-base leading-6 text-headerColor">
                Import summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VStack className="gap-6">
                <p className="text-sm text-secondaryText">
                  {result.summary.importedRows} employee records were imported
                  successfully.
                </p>
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {[
                    ["New employees", result.summary.newEmployees],
                    ["Merged rejoins", result.summary.mergedRejoins],
                    ["Separate rejoins", result.summary.separateRejoins],
                  ].map(([label, count]) => (
                    <Box
                      key={String(label)}
                      className="rounded-lg border border-borderColor p-4"
                    >
                      <p className="text-xs2 text-secondaryText">{label}</p>
                      <p className="mt-2 text-xl font-semibold text-headerColor">
                        {count}
                      </p>
                    </Box>
                  ))}
                </section>
                <VStack className="gap-2">
                  {result.rows.map((row) => (
                    <HStack
                      key={row.rowId}
                      className="justify-between border-b border-borderColor py-2 text-sm"
                    >
                      <span className="text-headerColor">
                        {row.employeeName}
                      </span>
                      <span className="text-secondaryText">
                        {importResultStrategyLabel(row.strategy)}
                      </span>
                    </HStack>
                  ))}
                </VStack>
              </VStack>
            </CardContent>
            <CardFooter className="w-full items-center justify-between gap-3 border-t border-borderColor !px-6 !py-4">
              <TertiaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                onClick={() => window.location.reload()}
              >
                Start another import
              </TertiaryCTAButton>
              <PrimaryCTAButton
                buttonSize={ButtonSize.MEDIUM}
                onClick={() => {
                  window.location.href = "/options/allEmployees";
                }}
              >
                Done
              </PrimaryCTAButton>
            </CardFooter>
          </Card>
        )}
        <BulkBoomerangResolutionDialog
          open={isBulkResolutionOpen}
          rows={selectedBoomerangRows}
          onClose={() => setIsBulkResolutionOpen(false)}
          onSave={applyBulkResolution}
        />
      </VStack>
    </main>
  );
}
